import React, { useState, useEffect } from "react";
import {
    Grid,
    Typography,
    Tooltip,
    IconButton,
    Switch,
    TextField,
    Button,
    Box,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import InputAdornment from "@mui/material/InputAdornment";

const PeakHoursConfig = ({ user, configTabSelector, validationErrors, handleInputChange }) => {
    // Initialize local state from existing configs or with one empty object for add
    const [peakConfigs, setPeakConfigs] = useState([
        {
            start_time: "",
            end_time: "",
            time_for_drop_off: configTabSelector.time_for_drop_off || 0,
            eta_to_customer_buffer_time_factor: configTabSelector.eta_to_customer_buffer_time_factor || 1,
            return_to_store_buffer_time_mins: configTabSelector.return_to_store_buffer_time_mins || 0,
            return_to_store_buffer_time_factor: configTabSelector.return_to_store_buffer_time_factor || 1
        },
    ]);

    // Load existing configs in edit case
    useEffect(() => {
        if (
            configTabSelector.peak_hours_eta_configs &&
            configTabSelector.peak_hours_eta_configs.length > 0
        ) {
            // Remove __typename to make objects editable
            const sanitizedConfigs = configTabSelector.peak_hours_eta_configs.map(
                ({ __typename, ...rest }) => ({ ...rest })
            );
            setPeakConfigs(sanitizedConfigs);
        }
    }, [configTabSelector.peak_hours_eta_configs]);

    // Handle input changes in the array
    const handleConfigChange = (index, field, value) => {
        // Create a new copy of peak_hours_eta_configs
        const updatedConfigs = [...peakConfigs]; // shallow copy of array
        updatedConfigs[index] = {
            ...updatedConfigs[index], // shallow copy of object
            [field]: value,          // update the specific field
        };

        handleInputChange(updatedConfigs, "peak_hours_eta_configs");
    };

    // Add new peak config
    const addPeakConfig = () => {
        const newConfig = {
            start_time: "",
            end_time: "",
            time_for_drop_off: configTabSelector.time_for_drop_off || 0,
            eta_to_customer_buffer_time_factor: configTabSelector.eta_to_customer_buffer_time_factor || 1,
            return_to_store_buffer_time_mins: configTabSelector.return_to_store_buffer_time_mins || 0,
            return_to_store_buffer_time_factor: configTabSelector.return_to_store_buffer_time_factor || 1
        };

        const updatedConfigs = [...peakConfigs, newConfig];

        // Update local state (UI)
        setPeakConfigs(updatedConfigs);

        // 🔥 Update Redux immediately
        handleInputChange(updatedConfigs, "peak_hours_eta_configs");
    };

    // Optional: Remove a peak config
    const removePeakConfig = (index) => {
        const newConfigs = peakConfigs.filter((_, i) => i !== index);
        setPeakConfigs(newConfigs);
        handleInputChange(newConfigs, "peak_hours_eta_configs");
    };

    return (
        <Grid container spacing={2}>
            {/* Enable Peak Hours */}
            <Grid item xs={12}>
                <Grid container>
                    <Grid item xs={8} display={"flex"}>
                        <Typography>
                            Enable Peak Hours
                            <Tooltip title="Toggle ON to use different buffer factors during peak times.">
                                <IconButton>
                                    <InfoIcon />
                                </IconButton>
                            </Tooltip>
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Switch
                            checked={configTabSelector.is_peak_hours_eta_enabled === "true" || false}
                            onChange={(ev) => {
                                const isEnabled = ev.target.checked.toString();

                                handleInputChange(isEnabled, "is_peak_hours_eta_enabled");
                                if (isEnabled === "true" &&
                                    (!configTabSelector.peak_hours_eta_configs ||
                                        !configTabSelector.peak_hours_eta_configs.length)) {

                                    handleInputChange(
                                        [{
                                            start_time: "",
                                            end_time: "",
                                            time_for_drop_off: configTabSelector.time_for_drop_off || 0,
                                            eta_to_customer_buffer_time_factor: configTabSelector.eta_to_customer_buffer_time_factor || 1,
                                            return_to_store_buffer_time_mins: configTabSelector.return_to_store_buffer_time_mins || 0,
                                            return_to_store_buffer_time_factor: configTabSelector.return_to_store_buffer_time_factor || 1
                                        }],
                                        "peak_hours_eta_configs"
                                    );
                                }
                            }}
                            inputProps={{ "aria-label": "controlled" }}
                            value={configTabSelector.is_peak_hours_eta_enabled === "true" || false}
                            disabled={!user.permissions.poi_editPoiConfiguration}
                        />
                    </Grid>
                </Grid>
            </Grid>

            {/* Peak Hours Configs */}
            {configTabSelector.is_peak_hours_eta_enabled === "true" &&
                peakConfigs.map((config, index) => (
                    <Grid item xs={12} key={index}>
                        <Box
                            sx={{
                                padding: 2,
                                border: "1px solid #ccc",
                                borderRadius: 2,
                                marginBottom: 2,
                                backgroundColor: "#fafafa",
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={4}>
                                    <TextField
                                        label="Start Time *"
                                        type="time"
                                        fullWidth
                                        value={config.start_time}
                                        onChange={(e) =>
                                            handleConfigChange(index, "start_time", e.target.value)
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        helperText={
                                            validationErrors[`peak_hours_start_time_${index}`] || ""
                                        }
                                        error={!!validationErrors[`peak_hours_start_time_${index}`]}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <TextField
                                        label="End Time *"
                                        type="time"
                                        fullWidth
                                        value={config.end_time}
                                        onChange={(e) =>
                                            handleConfigChange(index, "end_time", e.target.value)
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        helperText={
                                            validationErrors[`peak_hours_end_time_${index}`] || ""
                                        }
                                        error={!!validationErrors[`peak_hours_end_time_${index}`]}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <TextField
                                        label="Front Leg Time for Drop Off (Mins)"
                                        type="number"
                                        fullWidth
                                        value={config.time_for_drop_off}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            inputProps: { min: 0, max: 999, step: 1 },
                                            endAdornment: (
                                                <InputAdornment position="end">mins</InputAdornment>
                                            ),
                                        }}
                                        onChange={(e) => {
                                            let value = e.target.value;

                                            if (value === "") {
                                                handleConfigChange(index, "time_for_drop_off", "");
                                                return;
                                            }

                                            value = Math.floor(Math.abs(Number(value))); // remove decimals

                                            if (value > 999) value = 999;

                                            handleConfigChange(index, "time_for_drop_off", value);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <TextField
                                        label="Front Leg ETA Buffer Factor"
                                        type="number"
                                        fullWidth
                                        value={config.eta_to_customer_buffer_time_factor}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            inputProps: {
                                                min: 0.1,
                                                max: 10,
                                                step: 0.1
                                            },
                                            endAdornment: (
                                                <InputAdornment position="end"> x</InputAdornment>
                                            ),
                                        }}
                                        onChange={(e) => {
                                            let value = Math.abs(Number(e.target.value));
                                            if (value > 10) value = 10;
                                            handleConfigChange(index, "eta_to_customer_buffer_time_factor", value);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <TextField
                                        label="Return Leg Buffer Time (Mins)"
                                        type="number"
                                        fullWidth
                                        value={config.return_to_store_buffer_time_mins}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            inputProps: { min: 0, max: 999, step: 1 },
                                            endAdornment: (
                                                <InputAdornment position="end"> mins</InputAdornment>
                                            ),
                                        }}
                                        onChange={(e) => {
                                            let value = e.target.value;

                                            if (value === "") {
                                                handleConfigChange(index, "return_to_store_buffer_time_mins", "");
                                                return;
                                            }

                                            value = Math.floor(Math.abs(Number(value))); // remove decimals

                                            if (value > 999) value = 999;

                                            handleConfigChange(index, "return_to_store_buffer_time_mins", value);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <TextField
                                        label="Return Leg ETA Buffer Factor"
                                        type="number"
                                        fullWidth
                                        value={config.return_to_store_buffer_time_factor}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            inputProps: {
                                                min: 0.1,
                                                max: 10,
                                                step: 0.1
                                            },
                                            endAdornment: (
                                                <InputAdornment position="end"> x</InputAdornment>
                                            ),
                                        }}
                                        onChange={(e) => {
                                            let value = Math.abs(Number(e.target.value));
                                            if (value > 10) value = 10;
                                            handleConfigChange(index, "return_to_store_buffer_time_factor", value);
                                        }}
                                    />
                                </Grid>

                                {/* Optional remove button */}
                                <Grid item xs={12}>
                                    {peakConfigs.length > 1 && (
                                        <Button
                                            color="error"
                                            variant="outlined"
                                            onClick={() => removePeakConfig(index)}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                ))}

            {/* Add button */}
            {configTabSelector.is_peak_hours_eta_enabled === "true" && (
                <Grid item xs={12}>
                    <Button variant="contained" onClick={addPeakConfig}>
                        Add Peak Time Config
                    </Button>
                </Grid>
            )}
        </Grid>
    );
};

export default PeakHoursConfig;
